import { useEffect, useMemo, useRef, useState } from 'react'
import { AlarmClock, ChevronDown, ChevronUp, PanelLeft, Plus, Search, X } from 'lucide-react'
import { approxTokens, compactLimit, estimateContextTokens, tokensText } from '@shared/compaction'
import { resolveModel } from '@shared/models'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'
import { MessageList } from './MessageList'
import { Composer } from './Composer'
import { ChatSchedules } from './ChatSchedules'

/** Find-in-conversation: search THIS chat's loaded messages, step through matches (↑/↓), each
 *  jump scrolls to + flashes the message. ⌘/Ctrl+F opens it. Client-side → instant. */
function ChatSearch() {
  const messages = useStore((s) => s.messages)
  const activeChatId = useStore((s) => s.activeChatId)
  const jumpToMessage = useStore((s) => s.jumpToMessage)
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [idx, setIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const matches = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (term.length < 2) return []
    return messages.filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content?.toLowerCase().includes(term))
  }, [q, messages])

  useEffect(() => setIdx(0), [q, activeChatId]) // reset position on a new query OR when the chat changes
  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])
  // ⌘/Ctrl+F opens the in-chat search (only while a chat is open) instead of the browser's find.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'f' || e.key === 'F') && activeChatId) {
        e.preventDefault()
        setOpen(true)
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeChatId])

  const close = () => {
    setOpen(false)
    setQ('')
  }
  const go = (n: number) => {
    if (!matches.length || !activeChatId) return
    const next = ((n % matches.length) + matches.length) % matches.length
    setIdx(next)
    void jumpToMessage(activeChatId, matches[next].id)
  }

  if (!activeChatId) return null
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Search in this chat (⌘F)"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-fg-subtle transition-colors hover:bg-[var(--color-surface-2)] hover:text-fg"
      >
        <Search size={16} />
      </button>
    )
  }
  return (
    <div className="flex items-center gap-1 rounded-lg border border-[var(--line)] bg-[var(--color-surface-2)] pl-2 pr-1">
      <Search size={13} className="text-fg-faint" />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            go(e.shiftKey ? idx - 1 : idx + 1)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            close()
          }
        }}
        placeholder="Bu sohbette ara…"
        className="w-32 bg-transparent py-1.5 text-[12px] text-fg outline-none placeholder:text-fg-faint sm:w-44"
      />
      {q.trim().length >= 2 && (
        <span className="num shrink-0 px-0.5 text-[11px] text-fg-faint">
          {matches.length ? `${(((idx % matches.length) + matches.length) % matches.length) + 1}/${matches.length}` : '0'}
        </span>
      )}
      <button onClick={() => go(idx - 1)} disabled={!matches.length} title="Previous (Shift+Enter)" className="inline-flex h-7 w-6 items-center justify-center rounded text-fg-subtle hover:text-fg disabled:opacity-40">
        <ChevronUp size={14} />
      </button>
      <button onClick={() => go(idx + 1)} disabled={!matches.length} title="Next (Enter)" className="inline-flex h-7 w-6 items-center justify-center rounded text-fg-subtle hover:text-fg disabled:opacity-40">
        <ChevronDown size={14} />
      </button>
      <button onClick={close} title="Close (Esc)" className="inline-flex h-7 w-6 items-center justify-center rounded text-fg-subtle hover:text-fg">
        <X size={14} />
      </button>
    </div>
  )
}

/**
 * How full this chat's context is — measured: every answer records the prompt size the model itself
 * reported (system prompt + tools + memory + conversation) plus the answer. Click for the breakdown
 * and to compact now.
 */
function ContextMeter() {
  const messages = useStore((s) => s.messages)
  const activeChatId = useStore((s) => s.activeChatId)
  const compactedAt = useStore((s) => s.chats.find((c) => c.id === s.activeChatId)?.compactedAt ?? null)
  const compact = useStore((s) => s.compactChat)
  const streaming = useStore((s) => s.streaming)
  const chatModel = useStore((s) => s.chatModel)
  const compactAt = useStore((s) => s.settings?.compactAt ?? null)
  const focusSettings = useStore((s) => s.focusSettings)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', close)
    return () => window.removeEventListener('mousedown', close)
  }, [open])

  if (!activeChatId || messages.length < 2) return null
  const idx = messages.map((m) => m.role === 'assistant' && !!m.meta?.context).lastIndexOf(true)
  const measured = idx >= 0 ? messages[idx].meta!.context! : null
  let tokens: number
  let limit: number
  let windowSize: number
  let parts: { system: number; tools: number; memory: number; conversation: number } | null = null
  if (measured) {
    // Anything said after the measured answer (a new question, a wakeup line) joins the conversation.
    const after = approxTokens(messages.slice(idx + 1).reduce((a, m) => a + (m.meta?.quiet ? 0 : (m.content?.length ?? 0)), 0))
    tokens = measured.tokens + after
    limit = measured.limit
    windowSize = measured.window
    parts = { ...measured.parts, conversation: measured.parts.conversation + after }
  } else {
    const model = resolveModel(chatModel)
    tokens = estimateContextTokens(messages.filter((m) => compactedAt == null || m.createdAt > compactedAt))
    limit = compactLimit(model, compactAt)
    windowSize = model.context
  }
  const pct = Math.min(1, tokens / limit)
  const hot = pct > 0.85
  const tone = hot ? 'var(--color-down)' : pct > 0.6 ? 'var(--color-fg)' : 'var(--color-fg-subtle)'
  const rows: [string, number][] = parts
    ? [
        ['System prompt', parts.system],
        ['Tools', parts.tools],
        ['Memory', parts.memory],
        ['Conversation', parts.conversation],
      ]
    : []

  return (
    <div ref={box} className="relative hidden sm:block">
      <button
        onClick={() => setOpen(!open)}
        title={`Context ${tokensText(tokens)} of ${tokensText(limit)} — auto-compacts there`}
        className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] px-2 py-1.5 text-[11px] text-fg-subtle transition-colors hover:border-[var(--line)] hover:text-fg"
      >
        <span className="relative h-1.5 w-9 overflow-hidden rounded-full bg-[var(--color-inset)]">
          <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${Math.max(2, pct * 100)}%`, background: tone }} />
        </span>
        <span className={cn('num', hot && 'text-down')}>{busy ? 'Compacting…' : `${Math.round(pct * 100)}%`}</span>
      </button>
      {open && (
        <div className="glass absolute right-0 top-[calc(100%+6px)] z-50 w-[300px] p-3.5 text-left" style={{ borderRadius: 'var(--radius-lg)' }}>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[12.5px] font-semibold text-fg">Context</span>
            <span className="num text-[12px] text-fg-muted">
              {tokensText(tokens)} <span className="text-fg-faint">/ {tokensText(limit)}</span>
            </span>
          </div>
          <div className="relative mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--color-inset)]">
            <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${Math.max(1, pct * 100)}%`, background: tone }} />
          </div>
          {rows.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {rows.map(([label, n]) => (
                <div key={label} className="grid grid-cols-[92px_1fr_40px] items-center gap-2 text-[11.5px]">
                  <span className="text-fg-subtle">{label}</span>
                  <span className="relative h-1 overflow-hidden rounded-full bg-[var(--color-inset)]">
                    <span className="absolute inset-y-0 left-0 rounded-full bg-[var(--color-fg-subtle)]" style={{ width: `${Math.min(100, (n / limit) * 100)}%` }} />
                  </span>
                  <span className="num text-right text-fg-muted">{tokensText(n)}</span>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-[11px] leading-relaxed text-fg-faint">
            {measured
              ? `${measured.measured ? 'Measured by the model on the last answer' : 'Estimated after the last compaction'} · ${new Date(measured.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. `
              : 'Estimated — the next answer measures it. '}
            Auto-compacts at {tokensText(limit)} (the model’s window is {tokensText(windowSize)}). Older messages are summarized; the last 12 stay word
            for word.{' '}
            <button
              onClick={() => {
                setOpen(false)
                focusSettings('context')
              }}
              className="underline decoration-dotted underline-offset-2 hover:text-fg"
            >
              Change
            </button>
          </p>
          <button
            onClick={async () => {
              setOpen(false)
              setBusy(true)
              await compact()
              setBusy(false)
            }}
            disabled={busy || streaming}
            className="mt-3 w-full rounded-lg border border-[var(--line-soft)] px-2.5 py-1.5 text-[12px] text-fg-muted transition-colors hover:border-[var(--line)] hover:text-fg disabled:opacity-50"
          >
            Compact now
          </button>
        </div>
      )}
    </div>
  )
}

/** Schedule something in this chat (the AI wakes up here, with this conversation as its context). */
function ScheduleButton() {
  const chatId = useStore((s) => s.activeChatId)
  const count = useStore((s) => s.chatTasks.filter((t) => t.enabled && !t.doneAt).length)
  const openEditor = useStore((s) => s.openTaskEditor)
  if (!chatId) return null
  return (
    <button
      onClick={() => openEditor({ preset: { runIn: 'chat', chatId } })}
      title="Schedule the AI to wake up in this chat"
      className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg text-fg-subtle transition-colors hover:bg-[var(--color-surface-2)] hover:text-fg"
    >
      <AlarmClock size={16} />
      {count > 0 && <span className="num absolute -right-0.5 -top-0.5 min-w-[14px] rounded-full bg-[var(--color-surface-3)] px-1 text-[9px] leading-[14px] text-fg">{count}</span>}
    </button>
  )
}

export function Conversation() {
  const activeChatId = useStore((s) => s.activeChatId)
  const chats = useStore((s) => s.chats)
  const newChat = useStore((s) => s.newChat)
  const toggleSidebar = useStore((s) => s.toggleSidebar)
  // The header badge must show the model THIS chat actually runs on (per-chat picker),
  // not the server's env default — otherwise a grok-4.6 chat would still read "grok-4.3".
  const chatModel = useStore((s) => s.chatModel)
  const healthModel = useStore((s) => s.health?.model)
  const model = chatModel ?? healthModel
  const title = chats.find((c) => c.id === activeChatId)?.title ?? 'New chat'

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-[var(--color-base)]">
      <header className="flex h-[52px] shrink-0 items-center justify-between gap-3 border-b border-[var(--line-soft)] px-3.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <button
            onClick={() => toggleSidebar()}
            title="Toggle chat history"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-fg-subtle transition-colors hover:bg-[var(--color-surface-2)] hover:text-fg"
          >
            <PanelLeft size={17} />
          </button>
          <h2 className="truncate text-[13.5px] font-medium text-fg">{title}</h2>
        </div>
        <div className="flex items-center gap-2.5">
          {model && (
            <span className="num hidden text-[11px] text-fg-faint sm:inline">{model}</span>
          )}
          <ChatSearch />
          <ScheduleButton />
          <ContextMeter />
          <button
            onClick={() => newChat()}
            title="New chat"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--line-soft)] bg-[var(--color-surface-2)] px-2.5 py-1.5 text-[12px] text-fg-muted transition-colors hover:border-[var(--line)] hover:text-fg"
          >
            <Plus size={14} /> <span className="hidden sm:inline">New</span>
          </button>
          <div id="orb-dock" style={{ width: 46, height: 46 }} aria-hidden />
        </div>
      </header>
      <MessageList />
      <ChatSchedules />
      <Composer />
    </div>
  )
}
